import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { updateStorageQuantity } from 'services/apiRequests/storage.api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faMinus,
  faEdit,
  faHistory,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import ModalTemplate from 'PageTemplates/ModalTemplate';

const QuantityCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const CurrentQuantity = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #495057;
`;

const OperationTypeButtons = styled.div`
  .btn-group {
    width: 100%;
  }

  .btn {
    border-radius: 0;

    &:first-child {
      border-top-left-radius: 0.375rem;
      border-bottom-left-radius: 0.375rem;
    }

    &:last-child {
      border-top-right-radius: 0.375rem;
      border-bottom-right-radius: 0.375rem;
    }
  }
`;

const StorageUpdateModal = ({ show, onHide, item, onSuccess }) => {
  const [operationType, setOperationType] = useState('add'); // 'add', 'subtract', 'set'
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      // Reset form when modal opens
      setOperationType('add');
      setQuantity('');
      setReason('');
      setError('');
    }
  }, [show]);

  const calculateNewQuantity = () => {
    const currentQty = item?.quantity || 0;
    const inputQty = parseFloat(quantity) || 0;

    switch (operationType) {
      case 'add':
        return currentQty + inputQty;
      case 'subtract':
        return Math.max(0, currentQty - inputQty);
      case 'set':
        return Math.max(0, inputQty);
      default:
        return currentQty;
    }
  };

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      setError('Podaj prawidłową ilość');
      return;
    }

    if (!reason.trim()) {
      setError('Podaj powód zmiany');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const newQuantity = calculateNewQuantity();
      const changeAmount = parseFloat(quantity);

      // Wywołanie API do aktualizacji stanu magazynowego
      await updateStorageQuantity({
        itemId: item._id,
        operationType,
        quantity: changeAmount,
        newQuantity,
        reason: reason.trim(),
      });

      // Powiadomienie o sukcesie
      if (onSuccess) {
        onSuccess();
      }

      onHide();
    } catch (error) {
      console.error('Błąd podczas aktualizacji stanu:', error);
      setError(
        error.response?.data?.message ||
          'Wystąpił błąd podczas aktualizacji',
      );
    } finally {
      setLoading(false);
    }
  };

  const getOperationDescription = () => {
    const inputQty = parseFloat(quantity) || 0;
    const currentQty = item?.quantity || 0;
    const newQty = calculateNewQuantity();

    switch (operationType) {
      case 'add':
        return `Dodanie ${inputQty} szt. (${currentQty} + ${inputQty} = ${newQty})`;
      case 'subtract':
        return `Odjęcie ${inputQty} szt. (${currentQty} - ${inputQty} = ${newQty})`;
      case 'set':
        return `Ustawienie na ${inputQty} szt. (było ${currentQty}, będzie ${newQty})`;
      default:
        return '';
    }
  };

  if (!item || !show) return null;

  const isFormValid =
    quantity && parseFloat(quantity) > 0 && reason.trim();

  return (
    <ModalTemplate
      title={'Aktualizacja stanu magazynowego'}
      closeModal={onHide}
      variant="primary"
      size="md"
      onOk={handleSubmit}
      okButtonText={loading ? 'Zapisywanie...' : 'Zaktualizuj stan'}
      disabled={loading || !isFormValid}
    >
      <Row>
        <Col>
          <h6 className="text-muted mb-1">Produkt:</h6>
          <h5 className="mb-3">{item.name}</h5>

          <QuantityCard>
            <div className="text-muted mb-1">Aktualny stan</div>
            <CurrentQuantity>
              {item.quantity} {item.unit || 'szt.'}
            </CurrentQuantity>
          </QuantityCard>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Form.Group className="mb-3">
        <Form.Label>Typ operacji</Form.Label>
        <OperationTypeButtons>
          <div className="btn-group" role="group">
            <Button
              type="button"
              variant={
                operationType === 'add' ? 'success' : 'outline-success'
              }
              onClick={() => setOperationType('add')}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Dodaj
            </Button>
            <Button
              type="button"
              variant={
                operationType === 'subtract'
                  ? 'warning'
                  : 'outline-warning'
              }
              onClick={() => setOperationType('subtract')}
            >
              <FontAwesomeIcon icon={faMinus} className="me-1" />
              Odejmij
            </Button>
            <Button
              type="button"
              variant={
                operationType === 'set' ? 'primary' : 'outline-primary'
              }
              onClick={() => setOperationType('set')}
            >
              <FontAwesomeIcon icon={faEdit} className="me-1" />
              Ustaw
            </Button>
          </div>
        </OperationTypeButtons>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          {operationType === 'set' ? 'Nowa ilość' : 'Ilość do zmiany'}
        </Form.Label>
        <Form.Control
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="Wprowadź ilość"
          min="0"
          step="0.01"
          required
        />

        {quantity && (
          <div className="mt-2">
            <small className="text-info">
              <FontAwesomeIcon icon={faHistory} className="me-1" />
              {getOperationDescription()}
            </small>
          </div>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Powód zmiany *</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Opisz powód zmiany stanu magazynowego..."
          required
        />
        <Form.Text className="text-muted">
          Ten opis zostanie zapisany w historii zmian magazynowych
        </Form.Text>
      </Form.Group>
    </ModalTemplate>
  );
};

export default StorageUpdateModal;
